import axios from "axios";
import {API_URL} from "@/api/base";
import {auth} from "@/data/auth";

const authHeaders = async () => ({
    'Authorization': `Bearer ${await auth.getAccessToken()}`
});

export async function getList(company_id, query = '') {
    const queryString = new URLSearchParams(query).toString();
    return await axios.get(
        `${API_URL}/v1/certificate/list?company_id=${company_id}&${queryString}`,
        {headers: await authHeaders()}
    );
}

export async function getInfo(id) {
    return await axios.get(
        `${API_URL}/v1/certificate/${id}/info`,
        {headers: await authHeaders()}
    );
}

export async function getByProduct(product_id) {
    return await axios.get(
        `${API_URL}/v1/certificate/by-product/${product_id}`,
        {headers: await authHeaders()}
    );
}

export async function createItem(data) {
    return await axios.post(
        `${API_URL}/v1/certificate/create`,
        data,
        {headers: await authHeaders()},
    );
}

export async function createFromDocument(company_id, file) {
    const formData = new FormData();
    formData.append('company_id', company_id);
    formData.append('file', file);
    return await axios.post(
        `${API_URL}/v1/certificate/create-from-document`,
        formData,
        {headers: {...await authHeaders(), 'Content-Type': 'multipart/form-data'}},
    );
}

export async function updateItem(id, data) {
    return await axios.post(
        `${API_URL}/v1/certificate/${id}/update`,
        data,
        {headers: await authHeaders()},
    );
}

export async function removeItem(id) {
    return await axios.delete(
        `${API_URL}/v1/certificate/${id}/delete`,
        {headers: await authHeaders()}
    );
}

export async function uploadDocument(file) {
    const formData = new FormData();
    formData.append('file', file);
    return await axios.post(
        `${API_URL}/v1/certificate/upload/document`,
        formData,
        {headers: {...await authHeaders(), 'Content-Type': 'multipart/form-data'}},
    );
}

export async function linkProducts(id, product_ids) {
    return await axios.post(
        `${API_URL}/v1/certificate/${id}/link-products`,
        {product_ids},
        {headers: await authHeaders()},
    );
}

export async function unlinkProducts(id, product_ids) {
    return await axios.post(
        `${API_URL}/v1/certificate/${id}/unlink-products`,
        {product_ids},
        {headers: await authHeaders()},
    );
}

export async function autoLink(filters) {
    return await axios.post(
        `${API_URL}/v1/certificate/auto-link`,
        filters,
        {headers: await authHeaders()},
    );
}

export async function getOzonCertificates(storeId, filters) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/list`,
        filters,
        {headers: await authHeaders()},
    );
}

export async function uploadOzonCertificate(storeId, certificateId) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/upload/${certificateId}`,
        {},
        {headers: await authHeaders()},
    );
}

export async function bindOzonCertificateProducts(storeId, certificateId, product_ids = null) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/bind/${certificateId}`,
        {product_ids},
        {headers: await authHeaders()},
    );
}

export async function syncOzonCertificateProducts(storeId, certificateId, product_status_code = null) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/sync/${certificateId}/products`,
        {product_status_code},
        {headers: await authHeaders()},
    );
}

export async function syncAllOzonCertificateProducts(storeId, product_status_code = null) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/sync/products-all`,
        {product_status_code},
        {headers: await authHeaders()},
    );
}

export async function uploadNewOzonCertificates(storeId, filters) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/upload-new`,
        filters,
        {headers: await authHeaders()},
    );
}

export async function bindAllOzonCertificates(storeId, filters) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/bind-all`,
        filters,
        {headers: await authHeaders()},
    );
}

export async function syncOzonCertificates(storeId, filters) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/sync`,
        filters,
        {headers: await authHeaders()},
    );
}

export async function linkOzonCertificate(storeId, certificateId, ozon_certificate_id) {
    return await axios.put(
        `${API_URL}/v1/certificate/ozon/${storeId}/link/${certificateId}`,
        {ozon_certificate_id},
        {headers: await authHeaders()},
    );
}

export async function unlinkOzonCertificate(storeId, certificateId) {
    return await axios.delete(
        `${API_URL}/v1/certificate/ozon/${storeId}/link/${certificateId}`,
        {headers: await authHeaders()},
    );
}

export async function getOzonProducts(storeId, filters) {
    return await axios.post(
        `${API_URL}/v1/certificate/ozon/${storeId}/products`,
        filters,
        {headers: await authHeaders()},
    );
}

export async function checkLinks(urls) {
    return await axios.post(
        `${API_URL}/v1/certificate/links/check`,
        {urls},
        {headers: await authHeaders()},
    );
}

export async function createFromLinks(company_id, urls, product_ids = null) {
    const data = {company_id, urls};
    if (product_ids?.length) {
        data.product_ids = product_ids;
    }
    return await axios.post(
        `${API_URL}/v1/certificate/from-links`,
        data,
        {headers: await authHeaders()},
    );
}

export async function importLinks(company_id, file) {
    const formData = new FormData();
    formData.append('company_id', company_id);
    formData.append('file', file);
    return await axios.post(
        `${API_URL}/v1/certificate/import-links`,
        formData,
        {headers: {...await authHeaders(), 'Content-Type': 'multipart/form-data'}},
    );
}

export async function copyToCompany(source_company_id, target_company_id) {
    return await axios.post(
        `${API_URL}/v1/certificate/copy-to-company`,
        {source_company_id, target_company_id},
        {headers: await authHeaders()},
    );
}
